import {useCallback, useEffect, useState} from 'react';

import {useReadOnlyDataHook} from 'src/hooks/use-read-only-data-hook';
import {enforceUserMfa, getUserAuthenticationInfo} from 'src/openapi/admin/apis/UserAuthenticationInfoApi';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';

import {useCallbackWithRef} from '@pb/kit-util-use-callback-with-ref';

import type {SaveOptions} from 'src/components/save-with-reason/save-with-reason-button';

type Props = {
    userId: number,
    isEmailPrimaryMfaEnabled: boolean,
};

type MfaSettings = {
    mfaEnforced: boolean,
    emailPrimaryMfa: boolean,
};

const initialSettings: MfaSettings = {mfaEnforced: false, emailPrimaryMfa: false};

export const useUserMfaEnforced = ({userId, isEmailPrimaryMfaEnabled}: Props) => {
    const apiDispatch = useAppDispatch();
    const emailPrimaryMfaEnabled = isEmailPrimaryMfaEnabled === true;

    const getDispatchAction = useCallback(async (signal: AbortSignal) => {
        const response = await apiDispatch(getUserAuthenticationInfo({userId}, {signal}));
        if (!response.payload.success) {
            return {type: response.type, payload: response.payload};
        }
        const {mfaEnforced, emailPrimaryMfa} = response.payload.data;
        return {
            type: response.type,
            payload: {
                data: {mfaEnforced, emailPrimaryMfa},
                success: true as const,
                error: false as const,
            },
        };
    }, [userId, apiDispatch]);

    const {data: loadedData, loadData, loadingError, isLoading: isFetching, createAbortSignal} = useReadOnlyDataHook({
        getDispatchAction,
        initValue: initialSettings,
    });
    const [data, setData] = useState(initialSettings);
    const [dataInitial, setDataInitial] = useState(initialSettings);
    const [isDataLoading, setIsDataLoading] = useState(true);
    const isLoading = isFetching || isDataLoading;

    useEffect(() => {
        setData(loadedData);
        setDataInitial(loadedData);
    }, [loadedData]);

    useEffect(() => {
        setIsDataLoading(isFetching);
    }, [isFetching]);

    useEffect(() => {
        if (!emailPrimaryMfaEnabled && data.emailPrimaryMfa !== dataInitial.emailPrimaryMfa) {
            setData(current => ({...current, emailPrimaryMfa: dataInitial.emailPrimaryMfa}));
        }
    }, [emailPrimaryMfaEnabled, data.emailPrimaryMfa, dataInitial.emailPrimaryMfa]);

    const isChanged = data.mfaEnforced !== dataInitial.mfaEnforced
        || (emailPrimaryMfaEnabled && data.emailPrimaryMfa !== dataInitial.emailPrimaryMfa);

    const saveChanges = useCallbackWithRef(async (options?: SaveOptions) => {
        if (loadingError || isLoading) {
            return 'Load the current MFA settings before saving changes.';
        }
        const savedData = {
            mfaEnforced: data.mfaEnforced,
            emailPrimaryMfa: emailPrimaryMfaEnabled
                ? data.emailPrimaryMfa
                : data.mfaEnforced && dataInitial.emailPrimaryMfa,
        };
        const response = await apiDispatch(enforceUserMfa({
            reason: options?.reason,
            userId,
            enforceStatus: savedData.mfaEnforced,
            primaryEmailMfa: savedData.emailPrimaryMfa,
        }, {signal: createAbortSignal()}));
        if (response.payload.error) {
            const error = getError({error: response.payload.data, separator: '\n'});
            await loadData();
            return `${error} Some changes may have been applied. Review the current MFA settings before trying again.`;
        }
        setData(savedData);
        setDataInitial(savedData);
        return undefined;
    });

    const discardChanges = useCallback(() => setData(dataInitial), [dataInitial]);
    const validateChanges = useCallback(() => null, []);

    return {
        data, dataInitial, setData, isChanged, saveChanges, discardChanges,
        validateChanges, loadData, loadingError, isLoading,
    };
};
